<script lang="ts">
	import { fade } from 'svelte/transition';
	import { format } from 'svelte-i18n';
	//Stores
	import { devices } from '../../stores';
	//Helpers
	import { getApplianceType, getApplianceTypeByUsage } from '../../helpers/device_settings_helpers';
	import { convert, sortOn, capFirstChar } from '../../helpers/utilities';
	//Design system componets
	import {
		Text,
		ControlsContainer,
		Stack,
		ListItem,
		Scroll,
		HeadingWithButton,
		IconButton,
		Modal,
		Container,
		PieChart,
	} from '@Shelly/shelly-design-system';
	import { getDynamicPieChartConfig } from 'src/helpers/statistics/config/dynamic_statistics';
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { push } from 'svelte-spa-router';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let devicesCurrentConsList = {},
		gridColsCount = 1;

	const units = 'W';
	const chartHeight = 272;
	let showModal = false;

	const generateChartData = (devicesCurrentConsList) => {
		const byTypes = {},
			labels = [],
			keys = [],
			currentValues = [],
			colors = [];
		let byTypesAll = 0;
		let powerSourceAll = 0;

		Object.entries(devicesCurrentConsList).forEach((el: any) => {
			const deviceID = el[0],
				power = el[1]._power,
				type =
					$devices[deviceID]?.category === 'roller'
						? 'roller'
						: $devices[deviceID]?.category === 'light'
							? 'light'
							: getApplianceType($devices[deviceID]).usage;
			if (power) {
				if (type !== 'generated_energy' && type !== 'grid_source') {
					if (!byTypes.hasOwnProperty(type)) {
						byTypes[type] = 0;
					}
					byTypes[type] += power;
					byTypesAll += Math.abs(power);
				} else if (type === 'generated_energy' || type === 'grid_source') {
					powerSourceAll += Math.abs(power);
				}
			}
		});

		if (powerSourceAll > 0) {
			const unknownValue = powerSourceAll - byTypesAll;
			if (unknownValue > 0) {
				byTypes['unknown'] = unknownValue;
			}
		}

		Object.keys(byTypes)
			.sort()
			.forEach((v) => {
				const type = getApplianceTypeByUsage(v);
				labels.push(capFirstChar(type.title));
				keys.push(type.usage);
				colors.push(type.chartColor);
				currentValues.push(byTypes[v]);
			});

		return {
			keys,
			labels,
			currentValues,
			colors,
		};
	};

	const generateListData = (devicesCurrentConsList) => {
		const byTypes = {};
		const byTypesSource = {};
		let total = 0;
		let byTypesAll = 0;
		let powerSourceAll = 0;

		Object.entries(devicesCurrentConsList).forEach((el: any) => {
			const deviceID = el[0];
			const power = el[1]._power;
			const type =
				$devices[deviceID]?.category === 'roller'
					? 'roller'
					: $devices[deviceID]?.category === 'light'
						? 'light'
						: getApplianceType($devices[deviceID])?.usage;

			if (Math.abs(power) > 0) {
				if (type !== 'generated_energy' && type !== 'grid_source') {
					if (!byTypes.hasOwnProperty(type)) {
						const applianceType = getApplianceTypeByUsage(type);
						applianceType.power = 0;
						byTypes[type] = applianceType;
					}
					byTypes[type].power += power;
					byTypesAll += power;
				} else if (type === 'generated_energy' || type === 'grid_source') {
					if (!byTypesSource.hasOwnProperty(type)) {
						const applianceType = getApplianceTypeByUsage(type);
						applianceType.power = 0;
						byTypesSource[type] = applianceType;
					}
					byTypesSource[type].power += Math.abs(power);
					powerSourceAll += Math.abs(power);
				}
			}

			if (powerSourceAll > 0) {
				total = powerSourceAll;
			} else {
				total = byTypesAll;
			}
		});

		const unknownValue = powerSourceAll - byTypesAll;
		if (unknownValue > 0 && powerSourceAll > 0) {
			const applianceType = getApplianceTypeByUsage('unknown');
			applianceType.power = unknownValue;
			byTypes['unknown'] = applianceType;
		}

		return {
			types: sortOn(Object.values(byTypes), 'power', true),
			typesSource: sortOn(Object.values(byTypesSource), 'power', true),
			total,
		};
	};

	let switchedByTypeView = false;
	let logarithmic = false;

	const iconsCharts = {
		on: 'fa-solid fa-chart-bar',
		off: 'fa-solid fa-chart-pie-simple',
	};

	$: _chartData = generateChartData(devicesCurrentConsList);

	$: chartData = getDynamicPieChartConfig(_chartData);

	const openModal = () => {
		showModal = true;
	};

	const closeModal = () => {
		showModal = false;
	};

	const handleListClick = (type) => {
		if (type.usage === 'unknown') {
			openModal();
		} else {
			push(`/home/devices/-1?appliance_type=${type.value}`);
			resetFiltersAllDevicesList();
		}
	};
</script>

{#if gridColsCount != 3}
	<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
		<svelte:fragment slot="first">
			<HeadingWithButton class="mb-s">
				<Text slot="heading" styleType="controls-large" class="text-bold text-capitalize" noWrap>
					{$format('text_energy_by_appliance_type', {
						default: 'energy ay appliance type',
					})}
				</Text>
				<svelte:fragment slot="button">
					<!-- purely test user logic -->
					{#if isDevServerUser(['repo', 't1', 'nb'])}
						<IconButton
							class="mr-s"
							variant="secondary"
							icon={`fas fa-toggle-${logarithmic ? 'on' : 'off'}`}
							on:click={() => {
								logarithmic = !logarithmic;
							}}
							>{$format('label_logarithmic', {
								default: 'Logarithmic',
							})}</IconButton
						>
					{/if}
					<IconButton
						id="current-consumption-toggle-chart-btn"
						disabled={chartData.labels.length === 0}
						icon={`${iconsCharts[`${switchedByTypeView ? 'on' : 'off'}`]}`}
						aria-label={$format('button_toggle_chart_view', {
							default: `Switch to ${switchedByTypeView ? 'pie chart' : 'bar chart'} view`,
						})}
						on:click={() => (switchedByTypeView = !switchedByTypeView)}
					/>
				</svelte:fragment>
			</HeadingWithButton>
			{#if _chartData.labels.length === 0}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_current_consumption_no_active_pm_devices', {
							default:
								'There are currently no active power measuring devices. When the devices consume energy, you will se them here segmented by the "appliance type" you have assigned to them.',
						})}</Text
					>
				</div>
			{:else if chartData.loaded}
				{#if !switchedByTypeView}
					<div in:fade|global={{ duration: 300 }}>
						<Scroll
							style={gridColsCount == 1
								? 'height:auto; max-height:270px'
								: `height:${chartHeight}px`}
						>
							{@const listData = generateListData(devicesCurrentConsList)}
							<Stack gap="s" class="mb-l">
								{#each listData.types as type}
									<ListItem
										on:click={() => handleListClick(type)}
										title={capFirstChar(type.title)}
										icon={`fa fa-${type.icon}`}
										viewType="bar"
										actionBtn
										progress={{
											percent: logarithmic
												? (Math.log10(type.power) / Math.log10(listData.total)) *
													70
												: (type.power / listData.total) * 80,
											title: convert(type.power, units),
											color: type.color,
											showPercentBar: true,
										}}
									/>
								{/each}
							</Stack>
							<Stack gap="s">
								{#if listData.typesSource.length > 0}
									<Text styleType="body" class="mb-s"
										>{$format('title_consumption_sources', {
											default: 'Sources',
										})}</Text
									>
								{/if}
								{#each listData.typesSource as type}
									<ListItem
										title={capFirstChar(type.title)}
										icon={`fa fa-${type.icon}`}
										viewType="bar"
										actionBtn
										progress={{
											percent: (type.power / listData.total) * 100,
											title: convert(type.power, units),
											color: type.color,
											showPercentBar: true,
										}}
										on:click={() => handleListClick(type)}
									/>
								{/each}
							</Stack>
						</Scroll>
					</div>
				{:else}
					<PieChart {chartData} donut />
				{/if}
			{/if}
		</svelte:fragment>
	</ControlsContainer>
{:else}
	<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
		<svelte:fragment slot="first">
			<Text
				slot="heading"
				styleType="controls-large"
				class="text-bold text-capitalize mt-s mb-s"
				noWrap
			>
				{$format('text_energy_by_appliance_type', {
					default: 'energy ay appliance type',
				})}
			</Text>
			{#if _chartData.labels.length === 0}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_current_consumption_no_active_pm_devices', {
							default:
								'There are currently no active power measuring devices. When the devices consume energy, you will se them here segmented by the "appliance type" you have assigned to them.',
						})}</Text
					>
				</div>
			{:else if chartData?.loaded}
				<div in:fade|global={{ duration: 300 }}>
					<Scroll style={`height:${chartHeight}px`}>
						{@const listData = generateListData(devicesCurrentConsList)}
						<Stack gap="s" class="mb-l">
							{#each listData.types as type}
								<ListItem
									title={capFirstChar(type.title)}
									icon={`fa fa-${type.icon}`}
									viewType="bar"
									actionBtn
									progress={{
										percent: logarithmic
											? (Math.log10(type.power) / Math.log10(listData.total)) * 100
											: (type.power / listData.total) * 100,
										title: convert(type.power, units),
										color: type.color,
										showPercentBar: true,
									}}
									on:click={() => handleListClick(type)}
								/>
							{/each}
						</Stack>
						<Stack gap="s">
							{#each listData.typesSource as type}
								<ListItem
									title={capFirstChar(type.title)}
									icon={`fa fa-${type.icon}`}
									viewType="bar"
									actionBtn
									progress={{
										percent: (type.power / listData.total) * 100,
										title: convert(type.power, units),
										color: type.color,
										showPercentBar: true,
									}}
									on:click={() => handleListClick(type)}
								/>
							{/each}
						</Stack>
					</Scroll>
				</div>
			{/if}
		</svelte:fragment>
	</ControlsContainer>
	<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
		<svelte:fragment slot="first">
			<Text
				slot="heading"
				styleType="controls-large"
				class="text-bold text-capitalize mt-s mb-s"
				noWrap
			>
				{$format('text_energy_by_appliance_type', {
					default: 'energy ay appliance type',
				})}
			</Text>
			{#if _chartData.labels.length === 0}
				<div in:fade|global={{ duration: 300 }}>
					<Text class="mb-m" styleType="body"
						>{$format('description_current_consumption_no_active_pm_devices', {
							default:
								'There are currently no active power measuring devices. When the devices consume energy, you will se them here segmented by the "appliance type" you have assigned to them.',
						})}</Text
					>
				</div>
			{:else if chartData.loaded}
				<PieChart {chartData} donut />
			{/if}
		</svelte:fragment>
	</ControlsContainer>
{/if}

<Modal
	on:close={closeModal}
	{showModal}
	title={$format('label_info', { default: 'Info' })}
	backdropClose={true}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<Text class="mb-s" styleType="body"
				>{$format('current_consumption_unknown_title', {
					default: 'Unknown is calculated as follows:',
				})}</Text
			>
			<Text styleType="body"
				>{$format('current_consumption_unknown_description', {
					default: ' Grid source + Generated energy - All known appliances.',
				})}</Text
			>
		</Container>
	</svelte:fragment>
</Modal>
